import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Collapse, Badge } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Development-only permission debug panel
 * 
 * Displays:
 * - WordPress roles
 * - WordPress capabilities
 * - Business capabilities
 * - Current route requirements
 * 
 * Only visible in development mode.
 */
const PermissionDebugPanel: React.FC = () => {
  const { user, roles, capabilities, isSuperAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  const rolesArray = Array.from(roles);
  const capabilitiesArray = Array.from(capabilities);
  
  // Separate WP/WooCommerce caps from business caps
  const wpCapabilities = capabilitiesArray.filter(
    cap => !cap.startsWith('business_')
  );
  const businessCapabilities = capabilitiesArray.filter(
    cap => cap.startsWith('business_')
  );

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        zIndex: 9999,
        maxWidth: '400px',
        margin: '20px',
      }}
    >
      <Card className="shadow-lg">
        <CardHeader
          className="bg-primary text-white cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <FeatherIcon icon="shield" size={16} className="me-2" />
              Permission Debug
            </span>
            <FeatherIcon
              icon={isOpen ? 'chevron-down' : 'chevron-up'}
              size={16}
            />
          </div>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardBody style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {/* User Info */}
            {user && (
              <div className="mb-3">
                <h6 className="text-muted mb-2">User</h6>
                <div>
                  <strong>{user.name}</strong> ({user.email})
                </div>
                {isSuperAdmin() && (
                  <Badge color="danger" className="mt-1">
                    Super Admin
                  </Badge>
                )}
              </div>
            )}

            {/* Roles */}
            <div className="mb-3">
              <h6 className="text-muted mb-2">
                WordPress Roles ({rolesArray.length})
              </h6>
              {rolesArray.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {rolesArray.map(role => (
                    <Badge key={role} color="info">
                      {role}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted">No roles</span>
              )}
            </div>

            {/* WP Capabilities */}
            <div className="mb-3">
              <h6 className="text-muted mb-2">
                WP/WooCommerce Capabilities ({wpCapabilities.length})
              </h6>
              {wpCapabilities.length > 0 ? (
                <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {wpCapabilities.map(cap => (
                    <Badge key={cap} color="secondary" className="mb-1">
                      {cap}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted">No WP capabilities</span>
              )}
            </div>

            {/* Business Capabilities */}
            <div className="mb-3">
              <h6 className="text-muted mb-2">
                Business Capabilities ({businessCapabilities.length})
              </h6>
              {businessCapabilities.length > 0 ? (
                <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {businessCapabilities.map(cap => (
                    <Badge key={cap} color="success" className="mb-1">
                      {cap}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted">No business capabilities</span>
              )}
            </div>

            {/* Current Route */}
            <div>
              <h6 className="text-muted mb-2">Current Route</h6>
              <code className="text-primary">{window.location.pathname}</code>
            </div>
          </CardBody>
        </Collapse>
      </Card>
    </div>
  );
};

export default PermissionDebugPanel;
